/**
 * Backend Service - Handles all API calls to the User Backend
 * 
 * Three-Layer Architecture:
 * Frontend → User Backend → Admin Backend
 * 
 * No API keys in frontend - uses session-based authentication
 */

// Configuration - Read from localStorage (set during auth token exchange)
const getApiBaseUrl = () => localStorage.getItem('api_url') || 'https://app.loopai.in';

export const CONFIG = {
    get apiUrl() { return getApiBaseUrl(); }
};

console.log('Backend Service Configured (reads api_url from localStorage)');

// Helper to fetch image/video via proxy to avoid CORS issues with CDN URLs (e.g., fal.media, GCS)
// Includes retry logic for transient failures
async function fetchImageViaProxy(imageUrl, retries = 2) {
    // Validate URL before making request
    if (!imageUrl || 
        typeof imageUrl !== 'string' || 
        imageUrl === 'undefined' || 
        imageUrl === 'null' ||
        imageUrl === '' ||
        imageUrl.trim() === '') {
        console.warn('⚠️ Skipping invalid image URL:', imageUrl);
        return null;
    }
    
    // If already a data URI, return as-is
    if (imageUrl.startsWith('data:')) {
        return imageUrl;
    }
    
    // Skip blob URLs - they can't be proxied
    if (imageUrl.startsWith('blob:')) {
        return imageUrl;
    }
    
    // Validate it's a proper HTTP(S) URL
    if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
        console.warn('⚠️ Skipping non-HTTP URL:', imageUrl.substring(0, 50));
        return null;
    }
    
    // Additional validation - check URL is parseable
    try {
        new URL(imageUrl);
    } catch (e) {
        console.warn('⚠️ Skipping malformed URL:', imageUrl.substring(0, 50));
        return null;
    }
    
    let lastError = null;
    
    for (let attempt = 0; attempt <= retries; attempt++) {
        try {
            if (attempt > 0) {
                console.log(`🔄 Retry ${attempt}/${retries} for:`, imageUrl.substring(0, 80) + '...');
                // Wait before retry (exponential backoff)
                await new Promise(resolve => setTimeout(resolve, 500 * attempt));
            } else {
                console.log('🔄 Fetching via proxy:', imageUrl.substring(0, 100) + '...');
            }
            
            const response = await fetch(`${CONFIG.apiUrl}/api/proxy/fetch-image`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ image_url: imageUrl, return_base64: true, include_prefix: false })
            });
            
            if (!response.ok) {
                const errorText = await response.text().catch(() => 'Unknown error');
                throw new Error(`Proxy fetch failed: ${response.status} ${response.statusText} - ${errorText.substring(0, 200)}`);
            }
            
            const data = await response.json();
            
            // Use base64_raw if available (no prefix), otherwise use base64
            const base64Data = data.base64_raw || data.base64;
            
            if (base64Data) {
                const mimeType = data.mime_type || data.content_type || 'image/png';
                console.log('✅ Proxy fetch successful, MIME type:', mimeType);
                
                // Check if prefix already exists - don't add it again
                if (base64Data.startsWith('data:')) {
                    console.log('✅ Base64 already has prefix');
                    return base64Data;
                }
                
                // Add prefix only if not present
                return `data:${mimeType};base64,${base64Data}`;
            } else if (data.error) {
                throw new Error(data.error);
            }
            
            // Check for alternative response formats
            if (data.data) {
                const mimeType = data.mime_type || data.content_type || 'image/png';
                return `data:${mimeType};base64,${data.data}`;
            }
            
            console.warn('Proxy response structure:', Object.keys(data));
            throw new Error('No base64 data in proxy response');
            
        } catch (e) {
            lastError = e;
            if (attempt < retries) {
                console.warn(`⚠️ Attempt ${attempt + 1} failed, will retry:`, e.message.substring(0, 100));
            }
        }
    }
    
    console.error('❌ Proxy fetch failed after', retries + 1, 'attempts for', imageUrl.substring(0, 80), ':', lastError?.message);
    return null;
}

// Export the proxy fetch function for use in other services
export { fetchImageViaProxy };

// Utility function to convert multiple image URLs to base64 via proxy
// Use this immediately after image generation to avoid CORS issues with expiring CDN URLs
export async function convertImagesToBase64(imageUrls) {
    if (!imageUrls || !Array.isArray(imageUrls)) return [];
    
    const results = await Promise.all(
        imageUrls.map(async (url) => {
            if (!url) return null;
            if (url.startsWith('data:')) return url; // Already base64
            
            try {
                const base64 = await fetchImageViaProxy(url);
                return base64;
            } catch (e) {
                console.error('Failed to convert image:', url.substring(0, 50), e.message);
                return url; // Return original URL as fallback
            }
        })
    );
    
    return results.filter(Boolean);
}

// Helper to convert data URI or Blob URL to Blob using fetch
async function dataURItoBlob(dataURI) {
    if (!dataURI || typeof dataURI !== 'string') return null;
    if (!dataURI.startsWith('data:') && !dataURI.startsWith('blob:')) return null;
    
    try {
        const res = await fetch(dataURI);
        return await res.blob();
    } catch (e) {
        console.error("Error converting URI to blob:", e);
        return null;
    }
}

// Helper to convert URL back to Base64 (Hydration) with MIME type correction
// Uses proxy for external URLs to avoid CORS issues
async function urlToBase64(url) {
    // Validate URL before processing
    if (!url || 
        typeof url !== 'string' || 
        url === 'undefined' || 
        url === 'null' ||
        url === '' ||
        url.trim() === '') {
        console.warn('⚠️ Skipping invalid URL in urlToBase64:', url);
        return null;
    }
    
    // Handle Google Storage URLs
    let fetchUrl = url;
    if (url.startsWith('gs://')) {
        fetchUrl = url.replace('gs://', 'https://storage.googleapis.com/');
    }

    // If not an HTTP URL, return as-is (could be a local path or data URI)
    if (!fetchUrl.startsWith('http')) {
        // Check if it's a data URI
        if (url.startsWith('data:')) return url;
        // Otherwise skip it
        console.warn('⚠️ Skipping non-HTTP URL:', url.substring(0, 50));
        return null;
    }

    try {
        // Use proxy endpoint to avoid CORS issues with external CDNs (fal.media, etc.)
        const proxyResult = await fetchImageViaProxy(fetchUrl);
        if (proxyResult && proxyResult.startsWith('data:')) {
            return proxyResult;
        }
        
        // Proxy failed - try direct fetch as fallback (may work for some URLs)
        console.warn('⚠️ Proxy returned null, attempting direct fetch for:', fetchUrl.substring(0, 80));
        try {
            let response = await fetch(fetchUrl, { mode: 'cors' });

            if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);
            
            const blob = await response.blob();
            
            let finalBlob = blob;
            if (!blob.type || blob.type === 'application/octet-stream') {
                const lowerUrl = fetchUrl.toLowerCase();
                if (lowerUrl.endsWith('.png') || lowerUrl.includes('png')) finalBlob = new Blob([blob], { type: 'image/png' });
                else if (lowerUrl.endsWith('.jpg') || lowerUrl.endsWith('.jpeg') || lowerUrl.includes('jpeg') || lowerUrl.includes('jpg')) finalBlob = new Blob([blob], { type: 'image/jpeg' });
                else if (lowerUrl.endsWith('.webm') || lowerUrl.includes('webm')) finalBlob = new Blob([blob], { type: 'video/webm' });
                else if (lowerUrl.endsWith('.mp4') || lowerUrl.includes('mp4')) finalBlob = new Blob([blob], { type: 'video/mp4' });
                else if (lowerUrl.endsWith('.mp3') || lowerUrl.includes('mp3')) finalBlob = new Blob([blob], { type: 'audio/mpeg' });
                else {
                    finalBlob = new Blob([blob], { type: 'image/png' });
                }
            }

            return await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(finalBlob);
            });
        } catch (directFetchError) {
            console.warn('⚠️ Direct fetch also failed (CORS), keeping original URL:', directFetchError.message);
            // Return original URL - browser may be able to display it directly in <img> tag
            return fetchUrl;
        }
    } catch (e) {
        console.warn(`Failed to hydrate asset from ${url}, keeping URL. Error:`, e);
        return url; 
    }
}

// Recursive hydration for the project object
// For signed URLs: Just validate and pass through - no conversion needed
// Only convert to base64 when needed for canvas operations
async function hydrateProject(project) {
    console.log("🔄 Hydrating project - validating URLs...");
    const hydrated = JSON.parse(JSON.stringify(project));

    // Helper to validate URL - signed URLs from backend work directly
    const isValidMediaUrl = (url) => {
        if (!url || typeof url !== 'string') return false;
        if (url === 'undefined' || url === 'null' || url === '') return false;
        if (url.trim() === '') return false;
        
        // Valid data URI (base64)
        if (url.startsWith('data:') && url.length > 100 && url.includes('base64,')) {
            return true;
        }
        
        // Truncated/invalid data URI - skip
        if (url.startsWith('data:')) {
            console.warn('⚠️ Detected truncated/invalid data URI:', url.substring(0, 50));
            return false;
        }
        
        // Valid signed URL from backend (http/https)
        if (url.startsWith('http://') || url.startsWith('https://')) {
            return true;
        }
        
        // GCS URL
        if (url.startsWith('gs://')) {
            return true;
        }
        
        return false;
    };

    // Simply validate and pass through - no proxy conversion needed for display
    const validateUrl = (url, context = '') => {
        if (!url) return null;
        
        if (!isValidMediaUrl(url)) {
            console.warn(`⚠️ Skipping invalid URL for ${context}:`, String(url).substring(0, 80));
            return null;
        }
        
        // Convert gs:// to https:// for direct access
        if (url.startsWith('gs://')) {
            const httpsUrl = url.replace('gs://', 'https://storage.googleapis.com/');
            console.log(`✅ Converted GCS URL for ${context}`);
            return httpsUrl;
        }
        
        // Signed URLs work directly - just pass through
        if (url.startsWith('http')) {
            console.log(`✅ Valid signed URL for ${context}:`, url.substring(0, 60) + '...');
        }
        
        return url;
    };

    // 1. Characters - validate URLs, don't convert
    if (hydrated.characters && Array.isArray(hydrated.characters)) {
        for (const char of hydrated.characters) {
            if (char.image) char.image = validateUrl(char.image, `character "${char.name}" image`);
            if (char.audio?.data) char.audio.data = validateUrl(char.audio.data, `character "${char.name}" audio`);
            if (char.referenceImages && Array.isArray(char.referenceImages)) {
                char.referenceImages = char.referenceImages.map((img, i) => validateUrl(img, `character "${char.name}" ref image ${i}`)).filter(Boolean);
            }
            if (char.costumeAndAccessoryImages && Array.isArray(char.costumeAndAccessoryImages)) {
                char.costumeAndAccessoryImages = char.costumeAndAccessoryImages.map((img, i) => validateUrl(img, `character "${char.name}" costume image ${i}`)).filter(Boolean);
            }
        }
    }

    // 2. Scenes (Shots) - validate URLs, don't convert
    if (hydrated.scenes && Array.isArray(hydrated.scenes)) {
        for (let sceneIdx = 0; sceneIdx < hydrated.scenes.length; sceneIdx++) {
            const scene = hydrated.scenes[sceneIdx];
            if (scene.shots && Array.isArray(scene.shots)) {
                for (let shotIdx = 0; shotIdx < scene.shots.length; shotIdx++) {
                    const shot = scene.shots[shotIdx];
                    if (shot.startFrameImage) shot.startFrameImage = validateUrl(shot.startFrameImage, `scene ${sceneIdx + 1} shot ${shotIdx + 1} start frame`);
                    if (shot.endFrameImage) shot.endFrameImage = validateUrl(shot.endFrameImage, `scene ${sceneIdx + 1} shot ${shotIdx + 1} end frame`);
                    if (shot.video) shot.video = validateUrl(shot.video, `scene ${sceneIdx + 1} shot ${shotIdx + 1} video`);
                }
            }
        }
    }

    // 3. Assets - validate URLs, don't convert
    if (hydrated.assets && Array.isArray(hydrated.assets)) {
        for (const asset of hydrated.assets) {
            if (asset.data) asset.data = validateUrl(asset.data, `asset ${asset.name || 'unnamed'}`);
        }
    }

    console.log("✅ Project hydration complete - URLs validated and ready for display.");
    return hydrated;
}

// Helper function for canvas operations ONLY - use when you need base64 for image editing
export async function fetchImageAsBase64ForCanvas(imageUrl) {
    if (!imageUrl || typeof imageUrl !== 'string') return null;
    
    // Already base64
    if (imageUrl.startsWith('data:')) return imageUrl;
    
    // Try loading directly with crossOrigin first (preferred)
    try {
        return await new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                canvas.getContext('2d').drawImage(img, 0, 0);
                resolve(canvas.toDataURL('image/png'));
            };
            img.onerror = () => reject(new Error('Direct load failed'));
            img.src = imageUrl;
        });
    } catch (directError) {
        console.warn('Direct canvas load failed, trying proxy:', directError.message);
        
        // Fallback to proxy
        return await fetchImageViaProxy(imageUrl);
    }
}

// Function to upload media files to GCS
async function uploadMediaToGCS(userEmail, apiUrl, mediaFiles) {
    console.log(`🚀 Uploading ${mediaFiles.length} media files to GCS...`);
    
    // Log file sizes for debugging
    const totalSize = mediaFiles.reduce((sum, f) => sum + (f.blob?.size || 0), 0);
    console.log(`📊 Total upload size: ${(totalSize / 1024 / 1024).toFixed(2)} MB`);
    mediaFiles.forEach(f => {
        console.log(`  - ${f.filename}: ${((f.blob?.size || 0) / 1024).toFixed(1)} KB`);
    });
    
    if (mediaFiles.length === 0) return {};

    const formData = new FormData();
    formData.append('user_email', userEmail);
    
    mediaFiles.forEach(file => {
        formData.append('files', file.blob, file.filename);
    });
    
    try {
        // Get API key from localStorage
        const apiKey = localStorage.getItem('api_key');
        const storedUserEmail = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).email : null;
        
        const headers = {};
        if (apiKey) {
            headers['X-API-Key'] = apiKey;
        }
        if (storedUserEmail) {
            headers['X-User-Email'] = storedUserEmail;
        }
        
        const response = await fetch(`${apiUrl}/api/webhook/upload-media`, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: formData
        });
        
        if (!response.ok) {
            // Clone response before reading to avoid "body stream already read" error
            let errorDetail = '';
            try {
                const clonedResponse = response.clone();
                errorDetail = await clonedResponse.text();
            } catch (e) {
                errorDetail = 'Could not read error response';
            }
            console.error(`❌ Upload API error: ${response.status}`, errorDetail);
            throw new Error(`Upload API returned error: ${response.status} - ${errorDetail.substring(0, 200)}`);
        }
        
        const result = await response.json();
        
        const urlMap = {};
        const errors = [];

        if (result.files && Array.isArray(result.files)) {
            result.files.forEach((file) => {
                if (!file.error && file.gcs_url) {
                    urlMap[file.filename] = file.gcs_url;
                } else if (file.error) {
                    const errorMsg = `Server reported error uploading ${file.filename}: ${typeof file.error === 'object' ? JSON.stringify(file.error) : file.error}`;
                    console.error(errorMsg);
                    errors.push(errorMsg);
                }
            });
        }
        
        if (errors.length > 0 && Object.keys(urlMap).length === 0) {
            throw new Error(errors[0]);
        }
        
        console.log(`✅ Upload complete. Mapped ${Object.keys(urlMap).length} files.`);
        return urlMap;
    } catch (e) {
        console.error("Error in uploadMediaToGCS:", e);
        throw e;
    }
}

/**
 * Uploads a Data URI to the backend and returns a public URL
 */
export async function uploadDataUriToBackend(dataUri, userEmail) {
    const blob = await dataURItoBlob(dataUri);
    if (!blob) throw new Error("Invalid data URI or empty content for upload");
    
    const ext = blob.type.split('/')[1]?.split(';')[0] || 'bin';
    const filename = `temp_upload_${Date.now()}_${Math.random().toString(36).substring(7)}.${ext}`;
    
    console.log(`Preparing to upload ${filename} (${blob.size} bytes, ${blob.type})`);
    
    const urlMap = await uploadMediaToGCS(userEmail, CONFIG.apiUrl, [{ blob, filename }]);
    
    if (urlMap[filename]) {
        return urlMap[filename];
    }
    
    const returnedUrls = Object.values(urlMap);
    if (returnedUrls.length === 1) {
        console.warn(`Filename mismatch in upload response. Requested: ${filename}, using returned URL: ${returnedUrls[0]}`);
        return returnedUrls[0];
    }

    console.error("Upload response map:", JSON.stringify(urlMap, null, 2));
    throw new Error(`Upload processed but no valid URL returned for the file ${filename}.`);
}

export async function saveImagesToBackend(imageUrls, userEmail, projectName) {
    // Filter out undefined/null values first
    const validUrls = (imageUrls || []).filter(url => url && typeof url === 'string');
    
    if (validUrls.length === 0) {
        console.warn("saveImagesToBackend: No valid image URLs provided");
        return { success: true, urls: [] };
    }
    
    const base64Images = await Promise.all(
        validUrls.map(async (imageUrl) => {
            try {
                // If already a data URI, extract base64 part
                if (imageUrl.startsWith('data:')) return imageUrl.split(',')[1];
                
                // For HTTP URLs, use proxy to avoid CORS issues
                if (imageUrl.startsWith('http')) {
                    // Use proxy endpoint to fetch image
                    const proxyResult = await fetchImageViaProxy(imageUrl);
                    if (proxyResult && proxyResult.startsWith('data:')) {
                        return proxyResult.split(',')[1];
                    }
                    
                    // Fallback: try direct fetch (may fail for CDNs like fal.media)
                    console.warn('Proxy failed for saveImages, trying direct fetch:', imageUrl.substring(0, 50));
                    try {
                        const response = await fetch(imageUrl);
                        const blob = await response.blob();
                        return new Promise((resolve) => {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                                const res = reader.result;
                                resolve(res.split(',')[1]);
                            };
                            reader.onerror = () => resolve("");
                            reader.readAsDataURL(blob);
                        });
                    } catch (directFetchError) {
                        console.error('Direct fetch also failed:', directFetchError.message);
                        return "";
                    }
                }
                
                // If it's already a base64 string without prefix
                if (imageUrl.length > 100 && !imageUrl.includes('/')) {
                    return imageUrl;
                }
                console.warn("Unknown image URL format:", imageUrl.substring(0, 50));
                return "";
            } catch (e) {
                console.error("Failed to convert image to base64:", e);
                return "";
            }
        })
    );
    
    const validImages = base64Images.filter(img => img && img.length > 0);
    
    if (validImages.length === 0) {
        console.warn("saveImagesToBackend: No valid images after conversion");
        return { success: true, urls: [] };
    }

    try {
        const apiKey = localStorage.getItem('api_key');
        const storedUserEmail = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).email : null;
        
        const headers = { 'Content-Type': 'application/json' };
        if (apiKey) {
            headers['X-API-Key'] = apiKey;
        }
        if (storedUserEmail) {
            headers['X-User-Email'] = storedUserEmail;
        }
        
        const response = await fetch(`${CONFIG.apiUrl}/api/webhook/save-images`, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify({ user_email: userEmail, project_name: projectName, images: validImages })
        });
        
        if (!response.ok) throw new Error(`Failed to save images: ${response.statusText}`);
        return await response.json();
    } catch (error) {
        console.error("Error saving images to backend:", error);
        return { success: false, error: error.message };
    }
}

export async function deductCredits(userEmail, creditsUsed, projectId, projectName) {
    // Credits are now managed server-side automatically
    // This function is kept for backward compatibility but does nothing
    console.log(`Credits deduction requested: ${creditsUsed} (managed server-side)`);
    return { success: true, message: 'Credits managed server-side' };
}

// Cloud Rendering Functions
export const startCloudRender = async (projectData, userEmail) => {
    try {
        console.log('🎬 Starting video render...');
        const projectId = projectData.projectId || projectData.id || 'temp-' + Date.now();
        
        const apiKey = localStorage.getItem('api_key');
        const storedUserEmail = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).email : null;
        
        const headers = {
            'Content-Type': 'application/json'
        };
        
        if (apiKey) {
            headers['X-API-Key'] = apiKey;
        }
        if (storedUserEmail) {
            headers['X-User-Email'] = storedUserEmail;
        }
        
        console.log('🎬 Sending render request to:', `${CONFIG.apiUrl}/api/webhook/render-video`);
        
        const response = await fetch(`${CONFIG.apiUrl}/api/webhook/render-video`, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify({
                user_email: userEmail,
                project_id: projectId,
                project_data: projectData
            })
        });

        // Read response body as text first (only once!)
        const responseText = await response.text();
        console.log('🎬 Render response status:', response.status);
        console.log('🎬 Render response body:', responseText.substring(0, 500));

        if (!response.ok) {
            let errorDetail = responseText;
            try {
                const errorJson = JSON.parse(responseText);
                errorDetail = errorJson.detail || errorJson.error || errorJson.message || responseText;
            } catch (e) {
                // Keep original text if JSON parsing fails
            }
            throw new Error(`Render failed: ${response.status} - ${errorDetail}`);
        }

        // Parse JSON from the text we already read
        let result;
        try {
            result = JSON.parse(responseText);
        } catch (e) {
            throw new Error(`Invalid JSON response from server: ${responseText.substring(0, 200)}`);
        }
        
        console.log(`✅ Render job started: ${result.job_id}`);
        return { success: true, jobId: result.job_id };
    } catch (error) {
        console.error("Cloud render start error:", error);
        return { success: false, error: error.message };
    }
};

export const checkCloudRenderStatus = async (jobId) => {
    const apiKey = localStorage.getItem('api_key');
    const storedUserEmail = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).email : null;
    
    const headers = {
        'Content-Type': 'application/json'
    };
    
    if (apiKey) {
        headers['X-API-Key'] = apiKey;
    }
    if (storedUserEmail) {
        headers['X-User-Email'] = storedUserEmail;
    }
    
    const response = await fetch(
        `${CONFIG.apiUrl}/api/webhook/render-video-status?job_id=${jobId}`,
        {
            headers,
            credentials: 'include'
        }
    );

    // Read response as text first
    const responseText = await response.text();

    if (!response.ok) {
        throw new Error(`Status check failed: ${response.status} - ${responseText}`);
    }

    // Parse JSON from the text
    try {
        return JSON.parse(responseText);
    } catch (e) {
        throw new Error(`Invalid JSON response: ${responseText.substring(0, 200)}`);
    }
};

export const pollRenderUntilComplete = async (
    jobId,
    onProgress,
    maxWaitSeconds = 600
) => {
    const startTime = Date.now();
    
    while (true) {
        if ((Date.now() - startTime) / 1000 > maxWaitSeconds) {
            return { success: false, error: 'Rendering timeout - process took too long' };
        }

        try {
            const status = await checkCloudRenderStatus(jobId);
            
            if (onProgress) onProgress(status);

            if (status.status === 'completed') {
                console.log('✅ Render completed!');
                return { success: true, url: status.url };
            }

            if (status.status === 'failed') {
                console.error(`❌ Render failed: ${status.error}`);
                return { success: false, error: status.error || 'Rendering failed' };
            }
        } catch (error) {
            console.error('Error checking render status:', error);
        }

        await new Promise(resolve => setTimeout(resolve, 2000));
    }
};

// Project Saving
export const saveProjectToBackend = async (project, userEmail) => {
    console.log('💾 Starting optimized save...');
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 300000); 

    try {
        const mediaFiles = [];
        
        const getExt = (blob) => {
            const type = blob.type.split('/')[1]?.split(';')[0];
            return type || 'bin';
        };
        const isMedia = (str) => str && (str.startsWith('data:') || str.startsWith('blob:'));

        // 1. Characters (check if characters exist)
        const characters = project.characters || [];
        for (const char of characters) {
            if (isMedia(char.image)) {
                const blob = await dataURItoBlob(char.image);
                if (blob) mediaFiles.push({ blob, filename: `char_${char.id}_image.${getExt(blob)}` });
            }
            if (isMedia(char.audio?.data)) {
                const blob = await dataURItoBlob(char.audio.data);
                if (blob) mediaFiles.push({ blob, filename: `char_${char.id}_audio.${getExt(blob)}` });
            }
            if (char.referenceImages) {
                for (let idx = 0; idx < char.referenceImages.length; idx++) {
                    const img = char.referenceImages[idx];
                    if (isMedia(img)) {
                        const blob = await dataURItoBlob(img);
                        if (blob) mediaFiles.push({ blob, filename: `char_${char.id}_ref_${idx}.${getExt(blob)}` });
                    }
                }
            }
            if (char.costumeAndAccessoryImages) {
                for (let idx = 0; idx < char.costumeAndAccessoryImages.length; idx++) {
                    const img = char.costumeAndAccessoryImages[idx];
                    if (isMedia(img)) {
                        const blob = await dataURItoBlob(img);
                        if (blob) mediaFiles.push({ blob, filename: `char_${char.id}_costume_${idx}.${getExt(blob)}` });
                    }
                }
            }
        }

        // 2. Scenes (check if scenes exist)
        const scenes = project.scenes || [];
        for (const scene of scenes) {
            const shots = scene.shots || [];
            for (const shot of shots) {
                if (isMedia(shot.startFrameImage)) {
                    const blob = await dataURItoBlob(shot.startFrameImage);
                    if (blob) mediaFiles.push({ blob, filename: `shot_${shot.id}_start.${getExt(blob)}` });
                }
                if (isMedia(shot.endFrameImage)) {
                    const blob = await dataURItoBlob(shot.endFrameImage);
                    if (blob) mediaFiles.push({ blob, filename: `shot_${shot.id}_end.${getExt(blob)}` });
                }
                if (isMedia(shot.video)) {
                    const blob = await dataURItoBlob(shot.video);
                    if (blob) mediaFiles.push({ blob, filename: `shot_${shot.id}_video.${getExt(blob)}` });
                }
            }
        }

        // 3. Assets (check if assets exist)
        const assets = project.assets || [];
        for (const asset of assets) {
            if (isMedia(asset.data)) {
                const blob = await dataURItoBlob(asset.data);
                if (blob) mediaFiles.push({ blob, filename: `asset_${asset.id}.${getExt(blob)}` });
            }
        }

        // 4. Generated Ad Images (generatedImages or generatedAdImages)
        const generatedImagesArray = project.generatedImages || project.generatedAdImages || [];
        const generatedImageFilenames = []; // Track filenames for mapping later
        for (let idx = 0; idx < generatedImagesArray.length; idx++) {
            const img = generatedImagesArray[idx];
            if (isMedia(img)) {
                const blob = await dataURItoBlob(img);
                if (blob) {
                    const filename = `generated_image_${idx}.${getExt(blob)}`;
                    mediaFiles.push({ blob, filename });
                    generatedImageFilenames.push({ idx, filename });
                }
            }
        }
        console.log(`🖼️ Found ${generatedImageFilenames.length} generated images to upload`);

        console.log(`📦 Found ${mediaFiles.length} media files to upload`);

        let urlMap = {};
        let uploadFailed = false;
        if (mediaFiles.length > 0) {
            try {
                urlMap = await uploadMediaToGCS(userEmail, CONFIG.apiUrl, mediaFiles);
                console.log(`✅ Media upload phase complete. Mapped ${Object.keys(urlMap).length} files.`);
            } catch (uploadError) {
                console.warn('⚠️ Media upload failed, will use original URLs for generated images:', uploadError.message);
                uploadFailed = true;
            }
        }

        const optimizedProject = JSON.parse(JSON.stringify(project));

        const exactNameMap = {};
        Object.keys(urlMap).forEach(key => {
            const lastDotIndex = key.lastIndexOf('.');
            if (lastDotIndex !== -1) {
                const nameWithoutExt = key.substring(0, lastDotIndex);
                exactNameMap[nameWithoutExt] = urlMap[key];
            }
        });

        const findUrl = (name) => exactNameMap[name];

        // Replace blobs with URLs (check if characters exist)
        if (optimizedProject.characters && Array.isArray(optimizedProject.characters)) {
            optimizedProject.characters.forEach((char) => {
                if (isMedia(char.image)) {
                    const url = findUrl(`char_${char.id}_image`);
                    if (url) char.image = url;
                }
                if (isMedia(char.audio?.data)) {
                    const url = findUrl(`char_${char.id}_audio`);
                    if (url) char.audio.data = url;
                }
                if (char.referenceImages) {
                    char.referenceImages = char.referenceImages.map((img, idx) => {
                        if (isMedia(img)) return findUrl(`char_${char.id}_ref_${idx}`) || img;
                        return img;
                    });
                }
                if (char.costumeAndAccessoryImages) {
                    char.costumeAndAccessoryImages = char.costumeAndAccessoryImages.map((img, idx) => {
                        if (isMedia(img)) return findUrl(`char_${char.id}_costume_${idx}`) || img;
                        return img;
                    });
                }
            });
        }

        // Map scenes (check if scenes exist)
        if (optimizedProject.scenes && Array.isArray(optimizedProject.scenes)) {
            optimizedProject.scenes.forEach((scene) => {
                const shots = scene.shots || [];
                shots.forEach((shot) => {
                    if (isMedia(shot.startFrameImage)) {
                        const url = findUrl(`shot_${shot.id}_start`);
                        if (url) shot.startFrameImage = url;
                    }
                    if (isMedia(shot.endFrameImage)) {
                        const url = findUrl(`shot_${shot.id}_end`);
                        if (url) shot.endFrameImage = url;
                    }
                    if (isMedia(shot.video)) {
                        const url = findUrl(`shot_${shot.id}_video`);
                        if (url) shot.video = url;
                    }
                });
            });
        }

        // Map assets (check if assets exist)
        if (optimizedProject.assets && Array.isArray(optimizedProject.assets)) {
            optimizedProject.assets.forEach((asset) => {
                if (isMedia(asset.data)) {
                    const url = findUrl(`asset_${asset.id}`);
                    if (url) asset.data = url;
                }
            });
        }

        // Map generated images to their GCS URLs
        const uploadedGeneratedImages = [];
        
        if (!uploadFailed && generatedImageFilenames.length > 0) {
            // Upload succeeded - use GCS URLs
            for (const { idx, filename } of generatedImageFilenames) {
                const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
                const gcsUrl = exactNameMap[nameWithoutExt];
                if (gcsUrl) {
                    uploadedGeneratedImages.push(gcsUrl);
                    console.log(`✅ Generated image ${idx} mapped to GCS:`, gcsUrl.substring(0, 60) + '...');
                }
            }
        }
        
        // Also include any images that were already URLs (not base64)
        // OR use original URLs as fallback if upload failed
        const originalGeneratedImages = project.generatedImages || project.generatedAdImages || [];
        for (const img of originalGeneratedImages) {
            if (img && typeof img === 'string') {
                if (img.startsWith('http') && !uploadedGeneratedImages.includes(img)) {
                    // Already a URL - include it
                    uploadedGeneratedImages.push(img);
                    console.log(`📎 Including original URL:`, img.substring(0, 60) + '...');
                } else if (uploadFailed && img.startsWith('data:')) {
                    // Upload failed and this is base64 - we can't include it, log warning
                    console.warn(`⚠️ Cannot save base64 image without GCS upload`);
                }
            }
        }
        
        console.log(`🖼️ Total generated images for save: ${uploadedGeneratedImages.length}`);

        console.log('💾 Saving project metadata...');
        
        // Build payload matching the User Backend expected format
        // Email goes in header (X-User-Email), body only has project_data
        const payload = {
            project_data: {
                name: project.name,
                generatedImages: uploadedGeneratedImages,
                creditsUsed: project.creditsUsed || 0
            }
        };

        // Add projectId inside project_data if exists (for updates)
        if (project.projectId) {
            payload.project_data.projectId = project.projectId;
        }

        // Log final payload for debugging
        console.log('📤 Final save payload:', {
            project_data_name: payload.project_data.name,
            project_data_projectId: payload.project_data.projectId,
            creditsUsed: payload.project_data.creditsUsed,
            generatedImages_count: payload.project_data.generatedImages?.length || 0,
            generatedImages_preview: payload.project_data.generatedImages?.map(img => 
                img?.substring(0, 60) + (img?.length > 60 ? '...' : '')
            )
        });

        // Get API key and email from localStorage for headers
        const apiKey = localStorage.getItem('api_key');
        const storedUserEmail = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).email : null;
        
        const headers = {
            'Content-Type': 'application/json'
        };
        
        if (apiKey) {
            headers['X-API-Key'] = apiKey;
        }
        if (storedUserEmail) {
            headers['X-User-Email'] = storedUserEmail;
        }
        
        console.log('📨 Request headers:', {
            'X-API-Key': apiKey ? '***' + apiKey.slice(-4) : 'not set',
            'X-User-Email': storedUserEmail || 'not set'
        });

        const saveResponse = await fetch(`${CONFIG.apiUrl}/api/webhook/save-project`, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify(payload),
            signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!saveResponse.ok) {
            let errorDetail = '';
            try {
                // Clone the response to read it safely
                const clonedResponse = saveResponse.clone();
                errorDetail = await clonedResponse.text();
            } catch (e) {
                errorDetail = 'Could not read error response';
            }
            console.error('❌ Save project failed:', {
                status: saveResponse.status,
                statusText: saveResponse.statusText,
                errorDetail
            });
            throw new Error(`Server returned ${saveResponse.status}: ${errorDetail}`);
        }

        const result = await saveResponse.json();
        console.log(`✅ Project saved successfully. ID: ${result.project_id}`);
        
        return { success: true, project_id: result.project_id, optimizedProject };

    } catch (error) {
        clearTimeout(timeoutId);
        
        const isNetworkError = error.message === 'Failed to fetch' || error.message.includes('NetworkError');
        
        if (isNetworkError) {
             console.warn("Backend save skipped (offline/network error):", error.message);
        } else {
             console.error("Backend save failed:", error);
        }
        
        let errorMessage = error.message;
        if (error.name === 'AbortError') {
            errorMessage = "Request timed out after 5 minutes.";
        }
        
        return { success: false, error: errorMessage };
    }
};

// Get Projects
export const getProjectsFromBackend = async (userEmail) => {
    try {
        // Get API key from localStorage for webhook authentication
        const apiKey = localStorage.getItem('api_key');
        const storedUserEmail = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).email : null;
        
        const headers = {
            'Content-Type': 'application/json'
        };
        
        // Add API key header if available
        if (apiKey) {
            headers['X-API-Key'] = apiKey;
        }
        if (storedUserEmail) {
            headers['X-User-Email'] = storedUserEmail;
        }
        
        // Use GET /api/video-projects with project_type query param
        const response = await fetch(`${CONFIG.apiUrl}/api/video-projects?project_type=ad_creator`, {
            method: 'GET',
            headers,
            credentials: 'include'
        });

        // For 401/404, return empty projects without reading body
        if (response.status === 404 || response.status === 401) {
            return { success: true, projects: [] };
        }

        if (!response.ok) {
            return { success: false, projects: [], error: `HTTP ${response.status}` };
        }

        // Parse JSON response
        let result;
        try {
            result = await response.json();
        } catch (e) {
            console.warn('Failed to parse projects response as JSON');
            return { success: true, projects: [] };
        }
        
        // Handle both array response and object with projects field
        const rawProjects = Array.isArray(result) ? result : (result.projects || []);
        
        const projects = rawProjects.map((p) => ({
            id: p.id || p._id,
            name: p.name,
            lastModified: p.lastModified || p.last_modified || p.updated_at,
            projectId: p.project_id || p.projectId || p.id || p._id
        }));

        return { success: true, projects };
    } catch (error) {
        const isNetworkError = error.message === 'Failed to fetch' || error.message.includes('NetworkError');
        if (isNetworkError) {
            console.warn("Backend unavailable (offline mode):", error.message);
        } else {
            console.error("Backend fetch failed:", error);
        }
        return { success: false, projects: [], error: error.message };
    }
};

// Load Project
export const loadProjectFromBackend = async (projectId, userEmail) => {
    try {
        // Get API key from localStorage
        const apiKey = localStorage.getItem('api_key');
        const storedUserEmail = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).email : null;
        
        const headers = {
            'Content-Type': 'application/json'
        };
        
        if (apiKey) {
            headers['X-API-Key'] = apiKey;
        }
        if (storedUserEmail) {
            headers['X-User-Email'] = storedUserEmail;
        }
        
        const response = await fetch(`${CONFIG.apiUrl}/api/webhook/get-project`, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify({
                user_email: userEmail,
                project_id: projectId
            })
        });

        if (!response.ok) {
            const errorText = await response.text();
            if (response.status === 404 || errorText.includes('404') || errorText.includes('Project not found')) {
                console.warn(`Project ${projectId} not found on backend. Falling back to local.`);
                return { success: false, error: 'Project not found on server' };
            }
            throw new Error(`Failed to load project: ${response.status} ${errorText || response.statusText}`);
        }

        const result = await response.json();
        
        // Debug: Log what we received from backend
        console.log('📥 Received project from backend:', {
            projectId: result.project?.projectId,
            name: result.project?.name,
            charactersCount: result.project?.characters?.length,
            scenesCount: result.project?.scenes?.length,
            // Log first character image to see the format
            sampleCharImage: result.project?.characters?.[0]?.image?.substring(0, 100),
            // Log first shot start frame to see the format
            sampleShotImage: result.project?.scenes?.[0]?.shots?.[0]?.startFrameImage?.substring(0, 100)
        });
        
        const hydratedProject = await hydrateProject(result.project);
        return { success: true, project: hydratedProject };
    } catch (error) {
        if (error.message === 'Project not found on server') {
             return { success: false, error: error.message };
        }
        const isNetworkError = error.message === 'Failed to fetch' || error.message.includes('NetworkError');
        if (isNetworkError) {
             console.warn("Backend load skipped (offline mode):", error.message);
        } else {
             console.error("Backend load failed:", error);
        }
        return { success: false, error: error.message };
    }
};

// ============================================
// User Assets API - Fetch assets from User Backend
// ============================================

/**
 * Fetch user assets from the backend
 * @param {string} assetType - Type of asset: 'actors', 'costumes', 'logos', 'products'
 * @returns {Promise<{success: boolean, assets: Array, error?: string}>}
 */
export const fetchUserAssets = async (assetType = 'actors') => {
    try {
        const apiKey = localStorage.getItem('api_key');
        const storedUserEmail = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).email : null;
        
        const headers = {
            'Content-Type': 'application/json'
        };
        
        if (apiKey) {
            headers['X-API-Key'] = apiKey;
        }
        if (storedUserEmail) {
            headers['X-User-Email'] = storedUserEmail;
        }

        console.log(`📂 Fetching user assets: ${assetType}`);
        
        const response = await fetch(`${CONFIG.apiUrl}/api/user-assets/${assetType}`, {
            method: 'GET',
            headers,
            credentials: 'include'
        });

        if (!response.ok) {
            if (response.status === 404) {
                console.warn(`No ${assetType} assets found`);
                return { success: true, assets: [] };
            }
            const errorText = await response.text();
            throw new Error(`Failed to fetch ${assetType}: ${response.status} ${errorText}`);
        }

        const result = await response.json();
        
        // Handle different response formats
        const assets = result.assets || result.data || result[assetType] || result || [];
        
        console.log(`✅ Fetched ${assets.length} ${assetType} assets`);
        
        return { 
            success: true, 
            assets: Array.isArray(assets) ? assets : []
        };
    } catch (error) {
        console.error(`Error fetching ${assetType} assets:`, error);
        return { success: false, assets: [], error: error.message };
    }
};

/**
 * Fetch all user assets (actors, costumes, logos, products)
 * @returns {Promise<{success: boolean, assets: Object, error?: string}>}
 */
export const fetchAllUserAssets = async () => {
    try {
        const [actors, costumes, logos, products] = await Promise.all([
            fetchUserAssets('actors'),
            fetchUserAssets('costumes'),
            fetchUserAssets('logos'),
            fetchUserAssets('products')
        ]);

        return {
            success: true,
            assets: {
                actors: actors.assets || [],
                costumes: costumes.assets || [],
                logos: logos.assets || [],
                products: products.assets || []
            }
        };
    } catch (error) {
        console.error('Error fetching all user assets:', error);
        return { success: false, assets: {}, error: error.message };
    }
};
